/**
 * ConsolidationDocumentsTab — centrale operationele documentweergave.
 *
 * Bron: ÉÉN aggregator-endpoint `GET /admin/consolidations/{cid}/documents`
 * dat alles resolvet: consolidation-level documenten (label, manifest,
 * aanvullende uploads) en shipment-level documenten van alle ACTIEF
 * gekoppelde zendingen (Warehouse Receipt, Shipping Label, Paklijst,
 * Boekingsformulier, Factuur, handmatige koppelingen en uploads).
 *
 * Bekijken gebruikt `DocumentPreviewDialog` (inline iframe) — NOOIT meer
 * `window.open(blobUrl)`, dat opende een leeg tabblad in Chrome.
 *
 * Twee dossier-acties:
 *   • Volledig dossier downloaden → ZIP met ieder document als los bestand
 *   • Volledig dossier printen    → samengestelde print-PDF in de viewer
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Boxes, ChevronDown, ChevronUp, Download, Eye, FileCheck, FileCode,
  FileImage, FileSpreadsheet, FileText, Loader2, Package, Paperclip,
  Printer, RefreshCw, Trash2, Upload,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import DocumentPreviewDialog, { DossierNotice, unpackDocumentResponse, documentResponseError } from "../shared/DocumentPreviewDialog";
import { CustomsInvoiceAction } from "./CustomsInvoiceAction";
import { buildShipmentUrl } from "../../../utils/shipmentNavigation";

const TYPE_ICONS = {
  consolidation_label: <FileCheck className="w-3.5 h-3.5 text-yellow-700" />,
  warehouse_receipt: <FileCheck className="w-3.5 h-3.5 text-emerald-700" />,
  shipping_label: <FileText className="w-3.5 h-3.5 text-blue-700" />,
  packing_list: <FileText className="w-3.5 h-3.5 text-purple-700" />,
  booking_form: <FileText className="w-3.5 h-3.5 text-amber-700" />,
  invoice: <FileText className="w-3.5 h-3.5 text-slate-800" />,
  quote: <FileText className="w-3.5 h-3.5 text-slate-600" />,
  upload: <Paperclip className="w-3.5 h-3.5 text-cyan-700" />,
  manifest_sea: <FileImage className="w-3.5 h-3.5 text-blue-800" />,
  manifest_air: <FileImage className="w-3.5 h-3.5 text-sky-700" />,
  manifest_xlsx: <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-800" />,
  manifest_csv: <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-800" />,
  manifest_json: <FileCode className="w-3.5 h-3.5 text-slate-700" />,
  manifest_xml: <FileCode className="w-3.5 h-3.5 text-slate-700" />,
};

const TYPE_COLORS = {
  consolidation_label: "bg-yellow-50 text-yellow-900 border border-yellow-200",
  warehouse_receipt: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  shipping_label: "bg-blue-50 text-blue-800 border border-blue-200",
  packing_list: "bg-purple-50 text-purple-800 border border-purple-200",
  booking_form: "bg-amber-50 text-amber-800 border border-amber-200",
  invoice: "bg-slate-100 text-slate-800 border border-slate-200",
  quote: "bg-slate-50 text-slate-700 border border-slate-200",
  upload: "bg-cyan-50 text-cyan-900 border border-cyan-200",
  manifest_sea: "bg-blue-50 text-blue-900 border border-blue-200",
  manifest_air: "bg-sky-50 text-sky-900 border border-sky-200",
  manifest_xlsx: "bg-emerald-50 text-emerald-900 border border-emerald-200",
  manifest_csv: "bg-emerald-50 text-emerald-900 border border-emerald-200",
  manifest_json: "bg-slate-50 text-slate-800 border border-slate-200",
  manifest_xml: "bg-slate-50 text-slate-800 border border-slate-200",
};

const STATUS_LABELS_NL = {
  aangemaakt: "Klaar",
  concept: "Concept",
  gedownload: "Klaar",
  in_warehouse_ontvangen: "In magazijn",
  vertrokken: "Vertrokken",
  onderweg: "Onderweg",
  aangekomen: "Aangekomen",
  vrijgegeven: "Vrijgegeven",
  klaar_voor_afhalen: "Klaar voor afhalen",
  afgeleverd: "Afgeleverd",
};

function apiPath(url) {
  return (url || "").replace(/^\/api/, "");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch {
    return "—";
  }
}

function extractFilenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

function DocumentRow({ doc, onView, onPrint, onDownload, onDelete, canPreview, canDownload, busyDocId, unavailableDocs }) {
  const isBusy = busyDocId === doc.id;
  const issue = unavailableDocs?.find(item => item.id === doc.id)
    || (doc.unavailable_reason ? { reason: doc.unavailable_reason } : null);
  return (
    <div
      className="border-t border-gray-100 hover:bg-blue-50/30 text-xs py-2 px-3 space-y-2 md:space-y-0 md:grid md:grid-cols-12 md:gap-2 md:items-center"
      data-testid={`document-row-${doc.id}`}
    >
      {/* Mobiel: type + nummer op één regel, daarna meta, daarna acties.
          Desktop (md+): de originele 12-koloms tabelweergave. */}
      <div className="md:col-span-2 flex items-center gap-2 min-w-0">
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${TYPE_COLORS[doc.type] || "bg-slate-100 text-slate-700"}`}
          data-testid={`document-type-badge-${doc.id}`}
        >
          {TYPE_ICONS[doc.type]}
          <span className="truncate">{doc.type_label}</span>
        </span>
      </div>
      <div className="md:col-span-3 font-mono md:truncate break-all text-slate-700" title={doc.title}>
        {doc.number || "—"}
      </div>
      <div className="md:col-span-2 md:truncate break-words text-slate-600" title={doc.customer_name}>
        {doc.customer_name && doc.customer_name !== "-" ? doc.customer_name : "—"}
      </div>
      <div className={`md:col-span-1 text-[10px] uppercase font-medium ${issue ? "text-amber-800" : "text-slate-500"}`}
        title={issue?.reason} data-testid={`document-status-${doc.scope}-${doc.w_number || doc.consolidation_id}-${doc.id}`}>
        {issue ? "Niet beschikbaar" : STATUS_LABELS_NL[doc.status] || doc.status_label || "—"}
      </div>
      <div className="md:col-span-1 text-[10px] text-slate-500 font-mono">
        {fmtDate(doc.created_at)}
      </div>
      <div className="md:col-span-3 flex flex-wrap md:flex-nowrap items-center gap-1 md:justify-end">
        {canPreview && (
          <Button
            size="sm" variant="outline" onClick={() => onView(doc)} disabled={isBusy || !!doc.unavailable_reason}
            className="h-7 px-2 rounded-none text-[10px] font-bold uppercase border-slate-300 hover:bg-slate-100"
            data-testid={`document-view-${doc.id}`} title="Bekijken"
          >
            <Eye className="w-3 h-3 mr-1" /> Bekijken
          </Button>
        )}
        {canPreview && onPrint && (
          <Button
            size="sm" variant="outline" onClick={() => onPrint(doc)} disabled={isBusy || !!doc.unavailable_reason}
            className="h-7 px-2 rounded-none text-[10px] font-bold uppercase border-slate-300 hover:bg-slate-100"
            data-testid={`document-print-${doc.id}`} title="Printen"
          >
            <Printer className="w-3 h-3 mr-1" /> Printen
          </Button>
        )}
        {canDownload && <Button
          size="sm" onClick={() => onDownload(doc)} disabled={isBusy || !!doc.unavailable_reason}
          className="h-7 px-2 rounded-none text-[10px] font-bold uppercase bg-[#0055FF] text-white hover:bg-[#003fcc]"
          data-testid={`document-download-${doc.id}`} title="Downloaden"
        >
          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
          Download
        </Button>}
        {onDelete && doc.type === "upload" && (
          <button
            type="button" onClick={() => onDelete(doc)}
            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50"
            title="Verwijderen" data-testid={`document-delete-${doc.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function DocumentTable({ documents, ...actions }) {
  return (
    <div className="text-xs">
      <div className="hidden md:grid grid-cols-12 gap-2 items-center px-3 py-2 bg-gray-50 text-[10px] uppercase font-bold text-gray-600 border-b border-gray-200">
        <div className="col-span-2">Type</div>
        <div className="col-span-3">Nummer</div>
        <div className="col-span-2">Klant</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1">Datum</div>
        <div className="col-span-3 text-right">Acties</div>
      </div>
      {documents.map((doc) => (
        <DocumentRow key={doc.id} doc={doc} {...actions} />
      ))}
    </div>
  );
}

export default function ConsolidationDocumentsTab({ consolidationId }) {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [busyDocId, setBusyDocId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [preview, setPreview] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dossierBusy, setDossierBusy] = useState(null);
  const [dossierReport, setDossierReport] = useState(null);

  const canPreview = hasPermission?.("documents:preview") ?? true;
  const canDownload = hasPermission?.("documents:download") ?? false;
  const canEdit = hasPermission?.("consolidations:edit") ?? false;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDossierReport(null);
    try {
      const resp = await adminApi.get(`/admin/consolidations/${consolidationId}/documents`);
      setPayload(resp.data);
      const groups = resp.data?.shipment_documents || [];
      const initialExpanded = {};
      groups.forEach((g, idx) => {
        initialExpanded[g.shipping_label_id] = groups.length <= 3 || idx < 2;
      });
      setExpandedGroups(initialExpanded);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Onbekende fout");
    } finally {
      setLoading(false);
    }
  }, [consolidationId]);

  useEffect(() => { load(); }, [load]);

  const handleView = (doc) => {
    setPreview({
      title: doc.title || doc.type_label,
      subtitle: [doc.type_label, doc.number, doc.w_number].filter(Boolean).join(" · "),
      url: apiPath(doc.pdf_url),
      downloadUrl: apiPath(doc.download_url || doc.pdf_url),
      filename: `${doc.type}-${doc.number || doc.id}.pdf`,
    });
  };

  // Printen = dezelfde veilige preview, maar de printdialoog opent zodra het
  // iframe de PDF heeft geladen (autoPrint). Nooit window.open(blob).
  const handlePrint = (doc) => {
    setPreview({
      title: doc.title || doc.type_label,
      subtitle: [doc.type_label, doc.number, doc.w_number].filter(Boolean).join(" · "),
      url: apiPath(doc.pdf_url),
      downloadUrl: apiPath(doc.download_url || doc.pdf_url),
      filename: `${doc.type}-${doc.number || doc.id}.pdf`,
      autoPrint: true,
    });
  };

  const handleDownload = async (doc) => {
    if (busyDocId) return;
    setBusyDocId(doc.id);
    try {
      const resp = await adminApi.get(apiPath(doc.download_url || doc.pdf_url), {
        responseType: "blob",
      });
      if (!resp.data || resp.data.size === 0) {
        throw new Error("Document is leeg (0 bytes) — bron niet beschikbaar");
      }
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = extractFilenameFromDisposition(
        resp.headers?.["content-disposition"],
        `${doc.type}-${doc.number || doc.id}.pdf`,
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success(`${doc.type_label} gedownload`);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Geen toegang tot dit document");
      else if (e?.response?.status === 404) toast.error("Document niet gevonden of bestand ontbreekt");
      else toast.error(e?.response?.data?.detail || e?.message || "Download mislukt");
    } finally {
      setBusyDocId(null);
    }
  };

  const handleDeleteUpload = async (doc) => {
    if (!window.confirm(`Aanvullend document "${doc.number}" verwijderen?`)) return;
    try {
      await adminApi.delete(
        `/admin/consolidations/${consolidationId}/documents/uploads/${doc.source_id}`,
      );
      toast.success("Document verwijderd");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDossierDownload = async () => {
    setDossierBusy("zip");
    setDossierReport(null);
    try {
      const resp = await adminApi.get(
        `/admin/consolidations/${consolidationId}/dossier.zip`,
        { responseType: "blob", params: { include_report: true } },
      );
      const { blob, report } = await unpackDocumentResponse(resp);
      if (!blob || blob.size === 0) throw new Error("Dossier is leeg");
      setDossierReport(report);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = extractFilenameFromDisposition(
        resp.headers?.["content-disposition"], "Volledig_Dossier.zip",
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      const included = report?.included_count ?? resp.headers?.["x-dossier-included"];
      if (report?.status === "incomplete") toast.warning(`Onvolledig — ${included} documenten gedownload`, {
        description: `${report.missing_count} niet verwerkt. Zie de melding en het dossieroverzicht in de ZIP.`,
      });
      else toast.success(`Volledig dossier gedownload — ${included ?? "?"} documenten`);
    } catch (e) {
      toast.error(await documentResponseError(e));
    } finally {
      setDossierBusy(null);
    }
  };

  const handleDossierPrint = () => {
    setPreview({
      title: `Volledig dossier — ${payload?.consolidation?.consolidation_number || ""}`,
      subtitle: "Samengestelde print-PDF van alle dossierdocumenten",
      url: `/admin/consolidations/${consolidationId}/dossier-print.pdf?include_report=true`,
      downloadUrl: `/admin/consolidations/${consolidationId}/dossier-print.pdf?include_report=true`,
      filename: "Volledig_Dossier_Print.pdf",
      autoPrint: true,
    });
  };

  const toggleGroup = (sid) => {
    setExpandedGroups((prev) => ({ ...prev, [sid]: !prev[sid] }));
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-slate-500" data-testid="documents-loading">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-slate-400" />
        <p className="text-sm">Documenten laden…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 border border-red-200 bg-red-50 text-red-800" data-testid="documents-error">
        <p className="text-sm font-medium">Documenten konden niet worden geladen</p>
        <p className="text-xs mt-1">{error}</p>
        <Button
          size="sm" onClick={load} variant="outline"
          className="mt-3 rounded-none text-[10px] uppercase font-bold border-red-300 hover:bg-red-100"
          data-testid="documents-reload-btn"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Opnieuw proberen
        </Button>
      </div>
    );
  }

  const consolidationDocs = payload?.consolidation_documents || [];
  const shipmentGroups = payload?.shipment_documents || [];
  const totals = payload?.totals || {};
  const docTypes = payload?.doc_types || [];
  const rowActions = {
    onView: handleView,
    onPrint: handlePrint,
    onDownload: handleDownload,
    canPreview,
    canDownload,
    busyDocId,
    unavailableDocs: dossierReport?.skipped?.filter(item => item.kind === "unavailable"),
  };

  return (
    <div className="space-y-6" data-testid="documents-tab-root">
      {/* Dossier-actiebalk */}
      <div className="flex flex-wrap items-center gap-2 justify-between bg-slate-50 border border-slate-200 px-3 py-2">
        <div className="text-[11px] text-slate-600 uppercase tracking-wider font-semibold">
          {totals.all_documents || 0} document{totals.all_documents === 1 ? "" : "en"} —{" "}
          {totals.consolidation_documents || 0} consolidatie · {totals.shipment_documents || 0} shipment
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CustomsInvoiceAction consolidationId={consolidationId} onClose={load} />
          {canEdit && (
            <Button
              size="sm" variant="outline" onClick={() => setUploadOpen(true)}
              className="h-7 rounded-none text-[10px] uppercase font-bold border-slate-300 hover:bg-slate-100"
              data-testid="consolidation-upload-btn"
            >
              <Upload className="w-3 h-3 mr-1" /> Document uploaden
            </Button>
          )}
          <Button
            size="sm" variant="outline" onClick={load}
            className="h-7 rounded-none text-[10px] uppercase font-bold border-slate-300 hover:bg-slate-100"
            data-testid="documents-refresh-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Herladen
          </Button>
          {canPreview && <Button
            size="sm" variant="outline" onClick={handleDossierPrint}
            disabled={!!dossierBusy || (totals.all_documents || 0) === 0}
            className="h-7 rounded-none text-[10px] uppercase font-bold border-[#082c50] text-[#082c50] hover:bg-blue-50"
            data-testid="dossier-print-btn"
          >
            <Printer className="w-3 h-3 mr-1" /> Volledig dossier printen
          </Button>}
          {canDownload && <Button
            size="sm" onClick={handleDossierDownload}
            disabled={!!dossierBusy || (totals.all_documents || 0) === 0}
            className="h-7 rounded-none text-[10px] uppercase font-bold bg-[#082c50] hover:bg-[#0a3a6b] text-white"
            data-testid="dossier-download-btn"
          >
            {dossierBusy === "zip"
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <Download className="w-3 h-3 mr-1" />}
            Volledig dossier
          </Button>}
        </div>
      </div>

      <DossierNotice report={dossierReport} />
      {(totals.all_documents || 0) === 0 ? (
        <div
          className="text-center py-14 text-slate-500 border border-dashed border-slate-200"
          data-testid="documents-empty"
        >
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm font-medium">
            Voor deze consolidatie zijn nog geen documenten beschikbaar.
          </p>
          <p className="text-xs mt-1 text-slate-400">
            Documenten verschijnen automatisch zodra shipments gekoppeld zijn of
            een manifest wordt gegenereerd.
          </p>
        </div>
      ) : (
        <>
          {/* Consolidation-level documenten */}
          <section className="border border-gray-200" data-testid="consolidation-docs-section">
            <header className="bg-[#082c50] text-white px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileImage className="w-4 h-4" />
                <span className="text-[11px] uppercase font-bold tracking-wide">
                  Consolidatie documenten
                </span>
              </div>
              <span className="text-[10px] font-mono opacity-80">
                {consolidationDocs.length} item{consolidationDocs.length === 1 ? "" : "s"}
              </span>
            </header>
            {consolidationDocs.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-xs"
                   data-testid="consolidation-docs-empty">
                Geen consolidatie-brede documenten.
              </div>
            ) : (
              <DocumentTable
                documents={consolidationDocs}
                {...rowActions}
                onDelete={canEdit ? handleDeleteUpload : undefined}
              />
            )}
          </section>

          {/* Shipment-level documenten */}
          <section data-testid="shipment-docs-section">
            <header className="flex items-center gap-2 mb-2">
              <Boxes className="w-4 h-4 text-[#082c50]" />
              <span className="text-[11px] uppercase font-bold tracking-wide text-[#082c50]">
                Shipment documenten
              </span>
              <span className="text-[10px] text-slate-500">
                {shipmentGroups.length} zending{shipmentGroups.length === 1 ? "" : "en"}
              </span>
            </header>

            {shipmentGroups.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-xs border border-dashed border-slate-200"
                   data-testid="shipment-docs-empty">
                Geen zendingen aan deze consolidatie gekoppeld — dus ook geen zending-documenten.
              </div>
            ) : (
              <div className="space-y-3">
                {shipmentGroups.map((group) => {
                  const sid = group.shipping_label_id;
                  const isOpen = expandedGroups[sid] !== false;
                  const w = group.w_number || group.tracking_number;
                  return (
                    <div key={sid} className="border border-gray-200" data-testid={`shipment-group-${w}`}>
                      <button
                        type="button" onClick={() => toggleGroup(sid)}
                        className="w-full px-3 py-2 bg-slate-50 hover:bg-slate-100 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 text-left"
                        data-testid={`shipment-group-toggle-${w}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <Package className="w-4 h-4 text-slate-500 shrink-0" />
                          <span className="font-mono font-bold text-[#082c50] text-xs break-all">{w || "—"}</span>
                          {group.external_w_number && (
                            <span className="text-[10px] font-mono text-slate-500 break-all">
                              extern {group.external_w_number}
                            </span>
                          )}
                          {group.agency && (
                            <span className="text-[10px] text-slate-500">· {group.agency}</span>
                          )}
                          {group.customer_name && (
                            <span className="text-xs text-slate-600 break-words">· {group.customer_name}</span>
                          )}
                          {group.recipient_name && (
                            <span className="text-xs text-slate-500 break-words">→ {group.recipient_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-auto">
                          <span className="text-[10px] font-mono text-slate-500">
                            {group.documents.length} doc{group.documents.length === 1 ? "" : "s"}
                          </span>
                          {w && (
                            <a
                              href={buildShipmentUrl({ shipmentId: sid, wNumber: w, source: "consolidation-documents" })}
                              onClick={(e) => e.stopPropagation()}
                              className="text-[10px] uppercase font-bold text-[#0055FF] hover:underline"
                              data-testid={`shipment-group-open-${w}`}
                            >
                              Open zending
                            </a>
                          )}
                          {isOpen
                            ? <ChevronUp className="w-4 h-4 text-slate-500" />
                            : <ChevronDown className="w-4 h-4 text-slate-500" />}
                        </div>
                      </button>
                      {isOpen && (
                        group.documents.length === 0 ? (
                          <div className="text-center py-4 text-slate-400 text-[11px]"
                               data-testid={`shipment-group-empty-${w}`}>
                            Geen documenten voor deze zending.
                          </div>
                        ) : (
                          <DocumentTable documents={group.documents} {...rowActions} />
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <DocumentPreviewDialog
        open={!!preview}
        allowDownload={canDownload}
        onClose={() => setPreview(null)}
        title={preview?.title}
        subtitle={preview?.subtitle}
        url={preview?.url}
        downloadUrl={preview?.downloadUrl}
        fallbackFilename={preview?.filename}
        autoPrint={!!preview?.autoPrint}
        refreshBeforePrint={!!preview?.url?.includes("dossier-print.pdf")}
        onDossierReport={setDossierReport}
      />

      {uploadOpen && (
        <ConsolidationUploadDialog
          consolidationId={consolidationId}
          docTypes={docTypes}
          onClose={() => setUploadOpen(false)}
          onUploaded={async () => { setUploadOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

/* ─── Aanvullend consolidation-document uploaden ─────────────── */
function ConsolidationUploadDialog({ consolidationId, docTypes, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("other");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error("Selecteer een bestand"); return; }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);
      if (description.trim()) formData.append("description", description.trim());
      await adminApi.post(
        `/admin/consolidations/${consolidationId}/documents/uploads`,
        formData, { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Document toegevoegd aan consolidatie");
      onUploaded();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="consolidation-upload-dialog">
        <DialogHeader>
          <DialogTitle>Aanvullend consolidatiedocument</DialogTitle>
          <DialogDescription>
            Hoort direct bij deze consolidatie (bijv. aankoopfactuur, douanedocument,
            transportdocument). Max 50 MB.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Documenttype</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="consolidation-upload-doctype"><SelectValue /></SelectTrigger>
              <SelectContent>
                {docTypes.map((t) => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Omschrijving (optioneel)</Label>
            <input
              type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"
              placeholder="Bijv. Aankoopfactuur leverancier"
              data-testid="consolidation-upload-description"
            />
          </div>
          <div>
            <Label className="text-xs">Bestand</Label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full mt-1 text-xs"
              data-testid="consolidation-upload-file"
            />
            {file && (
              <div className="mt-1 text-xs text-slate-600 font-mono">
                {file.name} ({Math.round(file.size / 1024)} KB)
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button
            onClick={handleUpload} disabled={busy || !file}
            className="bg-[#0055FF] text-white"
            data-testid="consolidation-upload-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Uploaden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
